import { useState } from "react";
import { ArrowDownTray, CrossIcon, SpinnerIcon } from "../../Icons";
import { DocumentVault } from "../../../types/TradeFlow";
import DragAndDrop from "../../DragAndDrop";
import { toast } from "react-toastify";
import {
  useFileUploadMutation,
  useServeFileQuery,
} from "../../../store/apiSlices/common";
import { fileToBase64 } from "../../../utils";
import { useUpdateTradeFlowByIdAndTypeMutation } from "../../../store/apiSlices/tradeFlow";

interface DocumentUploadModalProps {
  documentVault: DocumentVault;
  row_id: string;
  step_id: string;
  toggleModal: () => void;
}

export default function UploadDocVault({
  documentVault,
  row_id,
  step_id,
  toggleModal,
}: DocumentUploadModalProps) {
  const [file, setFile] = useState<File>();

  const { data: uploadedDocFile, isLoading: isLoadingDocFile } =
    useServeFileQuery(documentVault.document_file!, {
      skip: !documentVault.document_file,
    });

  const [fileupload, { isLoading: fileUploadLoading }] =
    useFileUploadMutation();
  const [updateTradeFlowByIdAndType] = useUpdateTradeFlowByIdAndTypeMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0] || null;
    setFile(dropped);
  };

  const handleFileUpload = async () => {
    if (!file) return;

    const base64String = await fileToBase64(file);
    const payload = {
      base64File: base64String,
      folder_name: "deal_tracker",
    };

    const { data } = await fileupload(payload);

    // console.log(data);
    if (data.status === "success" && data.url) {
      toast.success(data.msg || "File uploaded successfully.");

      const payload = {
        row_id,
        step_id,
        type: "document_vault",
        type_id: documentVault.document_id,
        data: {
          document_id: documentVault.document_id,
          document_name: documentVault.document_name,
          document_file: data.url,
        },
      };

      await updateTradeFlowByIdAndType(payload);
      toggleModal();
    } else {
      toast.error("Something went wrong! please try again");
    }
  };

  const isSubmitDisabled = !file;

  return (
    <div className="space-y-5">
      <h3 className="text-gray-900">
        Upload document for - <i>{documentVault.document_name}</i>
      </h3>

      <DragAndDrop
        id="trade_flow_doc"
        onChange={handleFileChange}
        onDrop={handleDrop}
        file={file}
      />

      <div className={uploadedDocFile ? "h-64" : ""}>
        <FilePreview
          file={file}
          uploadedDocFile={uploadedDocFile}
          isLoadingDocFile={isLoadingDocFile}
          onRemove={() => setFile(undefined)}
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleFileUpload}
          disabled={isSubmitDisabled || fileUploadLoading}
          className={`px-5 py-2 rounded-lg shadow transition ${
            isSubmitDisabled
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-custom-blue-medium text-white hover:bg-custom-blue-medium/70"
          }`}
        >
          {fileUploadLoading ? (
            <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </div>
  );
}

export function FilePreview({
  file,
  uploadedDocFile,
  isLoadingDocFile,
  onRemove,
}: {
  file?: File;
  uploadedDocFile?: { fileUrl: string; mimeType: string };
  isLoadingDocFile: boolean;
  onRemove?: () => void;
}) {
  // 1️⃣ Show local file if exists
  if (file) {
    return (
      <>
        <p className="text-[#344054] text-center text-sm font-medium mt-3 truncate px-2">
          {file.name}
        </p>

        <div className="relative mt-3 flex justify-center">
          {file.type.startsWith("image/") && (
            <img
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="max-h-48 max-w-full rounded-lg shadow-md border border-gray-200"
            />
          )}

          {file.type === "application/pdf" && (
            <embed
              src={`${URL.createObjectURL(
                file
              )}#toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              className="w-full h-full rounded-lg border border-gray-200"
            />
          )}

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-0 right-4 p-1.5 bg-red-600 text-white rounded-full shadow hover:bg-red-700 transition"
              title="Remove file"
            >
              <CrossIcon className="size-4" />
            </button>
          )}
        </div>
      </>
    );
  }

  // 2️⃣ Show loading state if waiting for remote file
  if (isLoadingDocFile) {
    return (
      <div className="flex items-center justify-center">
        <SpinnerIcon className="size-8 text-gray-200 animate-spin fill-custom-blue-medium" />
      </div>
    );
  }

  // 3️⃣ Show remote file if available
  if (uploadedDocFile) {
    return (
      <>
        {uploadedDocFile.mimeType.startsWith("image/") && (
          <div className="relative flex justify-center">
            <img
              src={uploadedDocFile.fileUrl}
              alt="doc vault"
              className="max-h-48 max-w-full rounded-lg shadow-md border border-gray-200"
            />
            <a
              href={uploadedDocFile.fileUrl}
              download={"download_file"}
              className="absolute flex items-center gap-1 mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <ArrowDownTray className="size-5" />
              Download
            </a>
          </div>
        )}

        {uploadedDocFile.mimeType === "application/pdf" && (
          <embed
            src={`${uploadedDocFile.fileUrl}`}
            type="application/pdf"
            className="w-full h-full rounded-lg"
          />
        )}
      </>
    );
  }

  // 4️⃣ No file at all → render nothing
  return null;
}
